import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    CircularProgress,
    Collapse,
    Card,
    CardContent,
    Alert,
    useMediaQuery,
    InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudIcon from '@mui/icons-material/Cloud';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';
import { completeOnboarding, markOnboardingComplete, setNeedsOnboarding } from './onboardingSlice';
import { checkDriveStatus, getDriveAuthUrl } from '../../utils/driveApi';
import { toast } from 'react-toastify';

const STEPS = ['Your Details', 'Create Staff', 'Link Google Drive'];
const MAX_STAFF = 5;

// Reusable text field styles matching the app's design system
const getTextFieldStyles = (isMobile) => ({
    '& .MuiInputBase-root': {
        height: isMobile ? '44px' : '48px',
        borderRadius: '8px',
        fontSize: isMobile ? '14px' : '16px',
        backgroundColor: '#FFFFFF',
        '& fieldset': { borderColor: '#E0E0E0', borderWidth: '1px' },
        '&:hover fieldset': { borderColor: '#BDBDBD' },
        '&.Mui-focused fieldset': { borderColor: '#0063F7', borderWidth: '1px' },
    },
    '& .MuiInputBase-input': {
        padding: isMobile ? '10px 14px' : '12px 16px',
        color: '#212121',
        fontFamily: 'Roboto',
        fontWeight: 400,
        fontSize: isMobile ? '14px' : '16px',
        '&::placeholder': { color: '#9E9E9E', opacity: 1 },
    },
    '& .MuiInputLabel-root': {
        fontSize: isMobile ? '14px' : '16px',
        color: '#757575',
        '&.Mui-focused': { color: '#0063F7' },
        '&.MuiInputLabel-shrink': {
            padding: '0 6px',
        },
    },
});

const disabledFieldStyles = {
    '& .MuiInputBase-root': {
        backgroundColor: '#F5F5F5',
    },
    '& .MuiInputBase-input': {
        color: '#757575',
        cursor: 'not-allowed',
    },
};

/**
 * OnboardingModal - 3-step non-closable onboarding flow for first-time admins
 * Step 1: Admin Details (Name, Phone [greyed], Address)
 * Step 2: Staff Details (up to 5 staff with collapsible cards)
 * Step 3: Google Drive Linking
 * Success: Confirmation + Email invitation option
 */
const OnboardingModal = ({ open, adminPhone }) => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery('(max-width:768px)');
    const textFieldStyles = getTextFieldStyles(isMobile);
    const { onboardingLoading, onboardingError, createdStaff, failedStaff } = useSelector((state) => state.onboarding);

    const [currentStep, setCurrentStep] = useState(-1); // -1 = welcome screen
    const [submitting, setSubmitting] = useState(false);

    // Step 1: Admin details
    const [adminName, setAdminName] = useState('');
    const [adminAddress, setAdminAddress] = useState('');

    // Step 2: Staff list
    const [staffList, setStaffList] = useState([createEmptyStaff()]);
    const [expandedStaffIndex, setExpandedStaffIndex] = useState(0);
    const [showPasswords, setShowPasswords] = useState({});

    // Step 3: Drive
    const [driveLinked, setDriveLinked] = useState(false);
    const [driveLoading, setDriveLoading] = useState(false);
    const [driveCheckLoading, setDriveCheckLoading] = useState(true);

    // Step 4: Success
    const [showSuccess, setShowSuccess] = useState(false);
    const [sendingInvitations, setSendingInvitations] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);

    // Inline validation message for staff step
    const [staffValidationMsg, setStaffValidationMsg] = useState('');

    // Check drive status on mount and when component regains focus
    useEffect(() => {
        if (open && currentStep === 2) {
            checkDriveConnection();
        }
    }, [open, currentStep]);

    // Check if returning from Drive OAuth during onboarding
    useEffect(() => {
        if (open) {
            const driveReturn = localStorage.getItem('policytrackr_onboarding_drive_return');
            if (driveReturn) {
                try {
                    const savedState = JSON.parse(driveReturn);
                    // Restore to Drive step
                    setCurrentStep(2);
                    if (savedState.adminName) setAdminName(savedState.adminName);
                    if (savedState.adminAddress) setAdminAddress(savedState.adminAddress);
                    if (savedState.staffList) setStaffList(savedState.staffList);
                    // Check drive status immediately
                    checkDriveConnection();
                } catch (e) {
                    console.error('Error restoring onboarding state:', e);
                } finally {
                    localStorage.removeItem('policytrackr_onboarding_drive_return');
                }
            }
        }
    }, [open]);

    // Listen for focus (user returns from OAuth redirect)
    useEffect(() => {
        const handleFocus = () => {
            if (currentStep === 2) {
                checkDriveConnection();
            }
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [currentStep]);

    function createEmptyStaff() {
        return { branchName: '', email: '', password: '', whatsappNumber: '' };
    }

    const checkDriveConnection = useCallback(async () => {
        try {
            setDriveCheckLoading(true);
            const status = await checkDriveStatus(true); // force refresh
            setDriveLinked(status.linked);
        } catch (err) {
            console.error('Error checking drive status:', err);
        } finally {
            setDriveCheckLoading(false);
        }
    }, []);

    // Validation helpers
    const isAdminDetailsValid = () => {
        return adminName.trim().length > 0 && adminAddress.trim().length > 0;
    };

    const isStaffValid = (staff) => {
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(staff.email);
        const passwordValid = staff.password && staff.password.length >= 6;
        const branchValid = staff.branchName && staff.branchName.trim().length > 0;
        // WhatsApp is optional, but if provided must be 10 digits
        const phone = staff.whatsappNumber || '';
        const phoneValid = phone === '' || /^\d{10}$/.test(phone);
        return emailValid && passwordValid && branchValid && phoneValid;
    };

    const areAllStaffValid = () => {
        return staffList.every(isStaffValid);
    };

    const hasAnyStaffData = () => {
        return staffList.some(s =>
            s.branchName.trim() || s.email.trim() || s.password.trim() || s.whatsappNumber.trim()
        );
    };

    // Staff form handlers
    const handleStaffChange = (index, field, value) => {
        setStaffList(prev => {
            const updated = [...prev];
            if (field === 'whatsappNumber') {
                updated[index] = { ...updated[index], [field]: value.replace(/[^0-9]/g, '').substring(0, 10) };
            } else {
                updated[index] = { ...updated[index], [field]: value };
            }
            return updated;
        });
    };

    const addStaff = () => {
        if (staffList.length >= MAX_STAFF) return;
        setExpandedStaffIndex(staffList.length);
        setStaffList(prev => [...prev, createEmptyStaff()]);
    };

    const removeStaff = (index) => {
        if (staffList.length <= 1) return;
        setStaffList(prev => prev.filter((_, i) => i !== index));
        setExpandedStaffIndex(Math.max(0, expandedStaffIndex - 1));
    };

    const togglePasswordVisibility = (index) => {
        setShowPasswords(prev => ({ ...prev, [index]: !prev[index] }));
    };

    // Step navigation
    const handleNext = () => {
        if (currentStep < 2) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Submit onboarding (Step 2 → Step 3 transition saves data)
    const handleSubmitStaff = async () => {
        setStaffValidationMsg('');

        // Validate that at least one staff has all mandatory fields filled
        const staffWithData = staffList.filter(s =>
            s.branchName.trim() || s.email.trim() || s.password.trim() || s.whatsappNumber.trim()
        );

        if (staffWithData.length === 0) {
            setStaffValidationMsg('Please fill in at least one staff member\'s details.');
            setTimeout(() => setStaffValidationMsg(''), 1500);
            return;
        }

        // Validate all staff that have data
        const invalidStaff = staffWithData.filter(s => !isStaffValid(s));
        if (invalidStaff.length > 0) {
            setStaffValidationMsg('Please fill all mandatory fields for each staff member.');
            setTimeout(() => setStaffValidationMsg(''), 1500);
            return;
        }

        setSubmitting(true);
        try {
            await dispatch(completeOnboarding({
                adminDetails: {
                    name: adminName.trim(),
                    address: adminAddress.trim(),
                    phoneNumber: adminPhone,
                },
                staffList: staffWithData,
            })).unwrap();

            // Move to Drive linking step directly (no toast)
            setCurrentStep(2);
        } catch (err) {
            console.error('Onboarding submit error:', err);
            setStaffValidationMsg(err || 'Failed to complete onboarding. Please try again.');
            setTimeout(() => setStaffValidationMsg(''), 3000);
        } finally {
            setSubmitting(false);
        }
    };

    // Complete onboarding (after Drive is linked)
    const handleFinishOnboarding = async () => {
        try {
            await dispatch(markOnboardingComplete()).unwrap();
            setShowSuccess(true);
        } catch (err) {
            console.error('Error marking onboarding complete:', err);
            toast.error('Failed to finalize onboarding.');
        }
    };

    // Drive link handler
    const handleLinkDrive = async () => {
        try {
            setDriveLoading(true);

            // Save onboarding state to localStorage before redirecting
            // so we can restore it when Google redirects back
            localStorage.setItem('policytrackr_onboarding_drive_return', JSON.stringify({
                adminName: adminName,
                adminAddress: adminAddress,
                staffList: staffList,
                timestamp: Date.now(),
            }));

            const authUrl = await getDriveAuthUrl('desktop', 'onboarding');
            window.location.href = authUrl;
        } catch (err) {
            console.error('Error getting drive auth URL:', err);
            toast.error('Failed to start Google Drive connection');
            localStorage.removeItem('policytrackr_onboarding_drive_return');
            setDriveLoading(false);
        }
    };

    // Share credentials via email
    const handleShareCredentials = async () => {
        // Build staff list with credentials
        const staffWithData = staffList.filter(s =>
            s.branchName.trim() && s.email.trim() && s.password.trim()
        );

        if (staffWithData.length === 0) return;

        setSendingInvitations(false);
        setIsEmailLoading(true);

        // Hide loader and show "Sent" state after 3 seconds (optimistic UI)
        setTimeout(() => {
            setIsEmailLoading(false);
            setSendingInvitations(true);
        }, 3000);

        try {
            const { auth } = await import('../../firebase/firebaseConfig');
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error('No authenticated user');

            const token = await currentUser.getIdToken();
            const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const backendUrl = isLocal ? 'http://localhost:5001' : (import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001');

            const response = await fetch(`${backendUrl}/api/send-credentials`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    adminName: adminName.trim(),
                    staffList: staffWithData.map(s => ({
                        email: s.email,
                        password: s.password,
                        branchName: s.branchName,
                    })),
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to send emails');
            }

            if (data.failed && data.failed.length > 0) {
                toast.warning(`Sent to ${data.sent.length} staff. Failed for: ${data.failed.map(f => f.email).join(', ')}`);
            } else {
                toast.success(`Credentials sent to ${data.sent.length} staff member(s)!`);
            }
        } catch (err) {
            console.error('Error sending credentials:', err);
            toast.error(err.message || 'Failed to send credential emails');
            setSendingInvitations(false);
        }
    };

    // Close success dialog — navigate to admin finance page
    const handleCloseSuccess = () => {
        setShowSuccess(false);
        dispatch(setNeedsOnboarding(false));
        window.location.href = '/balance';
    };

    // =============== RENDER HELPERS ===============

    const renderWelcomeScreen = () => (
        <Box sx={{
            textAlign: 'center',
            py: isMobile ? 4 : 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <Box sx={{
                width: 80, height: 80, mx: 'auto', mb: 3,
                borderRadius: '50%', backgroundColor: '#E8F0FE',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#0063F7' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, fontFamily: 'Roboto' }}>
                Welcome to PolicyTrackr!
            </Typography>
            <Typography variant="body1" sx={{ color: '#616161', mb: 4, maxWidth: 400, mx: 'auto', lineHeight: 1.6 }}>
                Let's get you set up in just 3 quick steps. We'll collect your details,
                help you add team members, and link your Google Drive for secure document storage.
            </Typography>
            <Button
                variant="contained"
                onClick={() => setCurrentStep(0)}
                sx={{
                    width: isMobile ? '140px' : '160px',
                    height: isMobile ? '44px' : '48px',
                    borderRadius: '8px',
                    fontSize: isMobile ? '14px' : '16px',
                    backgroundColor: '#0063F7 !important',
                    color: '#FFFFFF',
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: '0.01em',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { backgroundColor: '#0051d4 !important' },
                    px: isMobile ? 3 : 4,
                }}
            >
                Get Started
            </Button>
        </Box>
    );

    const renderAdminDetailsStep = () => (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 2 : 3,
        }}>
            <Typography variant="body2" sx={{ color: '#616161', mb: 0, fontSize: isMobile ? '13px' : '14px' }}>
                Please provide your details to get started.
            </Typography>

            <TextField
                label="Name *"
                value={adminName}
                onChange={(e) => setAdminName(e.target.value)}
                fullWidth
                placeholder="Enter your full name"
                sx={textFieldStyles}
            />

            <TextField
                label="Mobile Number"
                value={adminPhone || ''}
                disabled
                fullWidth
                sx={{ ...textFieldStyles, ...disabledFieldStyles }}
            />

            <TextField
                label="Address *"
                value={adminAddress}
                onChange={(e) => setAdminAddress(e.target.value)}
                fullWidth
                placeholder="Enter your office/business address"
                sx={textFieldStyles}
            />
        </Box>
    );

    const renderStaffCard = (staff, index) => {
        const isExpanded = expandedStaffIndex === index;
        const valid = isStaffValid(staff);
        const hasData = staff.branchName.trim() || staff.email.trim();

        return (
            <Card
                key={index}
                variant="outlined"
                sx={{
                    borderRadius: '8px',
                    borderColor: valid ? '#4CAF50' : '#E0E0E0',
                    mb: 1.5,
                    transition: 'border-color 0.3s',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1,
                        cursor: 'pointer',
                        backgroundColor: isExpanded ? '#FAFAFA' : 'transparent',
                    }}
                    onClick={() => setExpandedStaffIndex(isExpanded ? -1 : index)}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {valid && <CheckCircleIcon sx={{ fontSize: 18, color: '#4CAF50' }} />}
                        <Typography variant="body2" sx={{
                            fontWeight: 500, color: '#212121',
                            '&:hover': {
                                fontWeight: 700,
                                textDecoration: 'underline',
                            },
                        }}>
                            {hasData ? `Staff ${index + 1}: ${staff.branchName || staff.email}` : `Staff ${index + 1}`}
                        </Typography>
                    </Box>
                    <Box>
                        {staffList.length > 1 && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeStaff(index); }}>
                                <DeleteIcon fontSize="small" sx={{ color: '#F04438' }} />
                            </IconButton>
                        )}
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                </Box>

                <Collapse in={isExpanded}>
                    <CardContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Branch Name *"
                            value={staff.branchName}
                            onChange={(e) => handleStaffChange(index, 'branchName', e.target.value)}
                            fullWidth
                            placeholder="Branch Location"
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Email *"
                            value={staff.email}
                            onChange={(e) => handleStaffChange(index, 'email', e.target.value)}
                            fullWidth
                            placeholder="staff@example.com"
                            type="email"
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Password *"
                            value={staff.password}
                            onChange={(e) => handleStaffChange(index, 'password', e.target.value)}
                            fullWidth
                            placeholder="Min 6 characters"
                            type={showPasswords[index] ? 'text' : 'password'}
                            helperText={staff.password && staff.password.length < 6 ? 'Password must be at least 6 characters' : ''}
                            error={staff.password.length > 0 && staff.password.length < 6}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => togglePasswordVisibility(index)}>
                                            {showPasswords[index] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="WhatsApp Number (Optional)"
                            value={staff.whatsappNumber}
                            onChange={(e) => handleStaffChange(index, 'whatsappNumber', e.target.value)}
                            fullWidth
                            placeholder="10-digit number"
                            sx={textFieldStyles}
                        />
                    </CardContent>
                </Collapse>
            </Card>
        );
    };

    const renderStaffDetailsStep = () => (
        <Box>
            <Typography variant="body2" sx={{ color: '#616161', mb: isMobile ? 2 : 3, fontSize: isMobile ? '13px' : '14px' }}>
                Add your team members. You can add up to {MAX_STAFF} staff during onboarding.
            </Typography>

            {staffValidationMsg && (
                <Alert severity="error" sx={{ mb: 2 }}>{staffValidationMsg}</Alert>
            )}

            {onboardingError && (
                <Alert severity="error" sx={{ mb: 2 }}>{onboardingError}</Alert>
            )}

            {failedStaff.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Some staff couldn't be created: {failedStaff.map(s => `${s.email} (${s.error})`).join(', ')}
                </Alert>
            )}

            {staffList.map((staff, index) => renderStaffCard(staff, index))}

            {staffList.length < MAX_STAFF && (
                <Button
                    startIcon={<AddIcon />}
                    onClick={addStaff}
                    sx={{
                        mt: 1,
                        textTransform: 'none',
                        color: '#0063F7',
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            fontWeight: 700,
                            textDecoration: 'underline',
                        },
                    }}
                >
                    Add another staff ({staffList.length}/{MAX_STAFF})
                </Button>
            )}
        </Box>
    );

    const renderDriveLinkStep = () => (
        <Box sx={{
            textAlign: 'center',
            py: 4,
        }}>
            <Box sx={{
                width: isMobile ? 56 : 64, height: isMobile ? 56 : 64, mx: 'auto', mb: 2,
                borderRadius: '50%',
                backgroundColor: driveLinked ? '#E8F5E9' : '#E8F0FE',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <CloudIcon sx={{ fontSize: isMobile ? 28 : 32, color: driveLinked ? '#4CAF50' : '#0063F7' }} />
            </Box>

            {driveCheckLoading ? (
                <CircularProgress size={24} sx={{ my: 2 }} />
            ) : driveLinked ? (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32', mb: 1, fontSize: isMobile ? '18px' : '20px' }}>
                        Google Drive Connected!
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#616161', mb: 3, fontSize: isMobile ? '13px' : '14px' }}>
                        Your Google Drive is linked. All policy PDFs will be stored securely in your Drive.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleFinishOnboarding}
                        sx={{
                            width: isMobile ? '140px' : '200px',
                            height: isMobile ? '44px' : '48px',
                            borderRadius: '8px',
                            fontSize: isMobile ? '14px' : '16px',
                            backgroundColor: '#0063F7 !important',
                            color: '#FFFFFF',
                            fontFamily: 'Roboto',
                            lineHeight: '24px',
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': { backgroundColor: '#0051d4 !important' },
                            px: isMobile ? 3 : 4,
                        }}
                    >
                        Complete Setup
                    </Button>
                </>
            ) : (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '18px' : '20px' }}>
                        Link your Google Drive
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#616161', mb: 3, maxWidth: 400, mx: 'auto', fontSize: isMobile ? '13px' : '14px' }}>
                        Connect your Google Drive to store policy PDFs securely. Only minimal permissions are requested.
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={driveLoading ? <CircularProgress size={18} color="inherit" /> : <CloudIcon />}
                        onClick={handleLinkDrive}
                        disabled={driveLoading}
                        sx={{
                            width: isMobile ? '220px' : '240px',
                            height: isMobile ? '44px' : '48px',
                            borderRadius: '8px',
                            fontSize: isMobile ? '14px' : '16px',
                            backgroundColor: '#0063F7 !important',
                            color: '#FFFFFF',
                            fontFamily: 'Roboto',
                            lineHeight: '24px',
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#0051d4 !important' },
                            px: isMobile ? 3 : 4,
                        }}
                    >
                        Connect Google Drive
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#9E9E9E' }}>
                        * This will open a Google sign-in page. Only the "drive.file" permission is requested.
                    </Typography>
                </>
            )}
        </Box>
    );

    const renderSuccessScreen = () => {
        const staffWithData = staffList.filter(s =>
            s.branchName.trim() && s.email.trim() && s.password.trim()
        );

        return (
            <Box sx={{ textAlign: 'center', pt: 0, pb: 1 }}>
                <Box sx={{
                    width: 80, height: 80, mx: 'auto', mb: 3,
                    borderRadius: '50%', backgroundColor: '#E8F5E9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, fontFamily: 'Roboto' }}>
                    🎉 Hi {adminName || 'there'}!
                </Typography>
                <Typography variant="body1" sx={{ color: '#616161', mb: 1, maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
                    You have been successfully onboarded to PolicyTrackr.
                </Typography>

                {staffWithData.length > 0 && (
                    <>
                        <Typography variant="body2" sx={{ color: '#424242', mt: 2, mb: 2, maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
                            Please share your branch credentials with staff so they can start managing your policies.
                        </Typography>

                        <Box sx={{ mt: 1, mb: 3, textAlign: 'left' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#212121' }}>
                                Staff Credentials:
                            </Typography>
                            {staffWithData.map((staff, i) => (
                                <Box key={i} sx={{
                                    mb: 1.5, p: 1.5,
                                    borderRadius: '8px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E0E0E0',
                                    position: 'relative',
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            const text = `Branch: ${staff.branchName}\nEmail: ${staff.email}\nPassword: ${staff.password}`;
                                            navigator.clipboard.writeText(text);
                                        }}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            color: '#757575',
                                            '&:hover': { color: '#0063F7' },
                                        }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#212121' }}>
                                        {staff.branchName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#616161', display: 'block' }}>
                                        Email: {staff.email}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#616161', display: 'block' }}>
                                        Password: {staff.password}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                <Box sx={{ display: 'flex', gap: isMobile ? 2 : 3, justifyContent: 'center', mt: 6, width: '100%' }}>
                    <Button
                        variant="outlined"
                        onClick={handleCloseSuccess}
                        sx={{
                            flex: 1,
                            height: isMobile ? '44px' : '48px',
                            borderRadius: '8px',
                            fontSize: isMobile ? '14px' : '16px',
                            borderWidth: '1px',
                            borderColor: '#B00020',
                            color: '#B00020',
                            fontFamily: 'Roboto',
                            lineHeight: '24px',
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'inherit',
                                color: '#B00020',
                                boxShadow: 10,
                            },
                        }}
                    >
                        Go to Dashboard
                    </Button>
                    {staffWithData.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={isEmailLoading ? <CircularProgress size={18} color="inherit" /> : (sendingInvitations ? null : <EmailIcon />)}
                            onClick={handleShareCredentials}
                            disabled={sendingInvitations || isEmailLoading}
                            sx={{
                                flex: 1,
                                height: isMobile ? '44px' : '48px',
                                borderRadius: '8px',
                                fontSize: isMobile ? '14px' : '16px',
                                backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0063F7 !important'),
                                color: '#FFFFFF',
                                fontFamily: 'Roboto',
                                lineHeight: '24px',
                                letterSpacing: '0.01em',
                                textTransform: 'none',
                                boxShadow: 'none',
                                whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0051d4 !important') },
                                '&.Mui-disabled': {
                                    backgroundColor: sendingInvitations ? '#4CAF50 !important' : (isEmailLoading ? '#BDBDBD !important' : '#0063F7 !important'),
                                    color: '#FFFFFF !important'
                                },
                                px: isMobile ? 3 : 4,
                            }}
                        >
                            {isEmailLoading ? 'Sharing...' : (sendingInvitations ? 'Sent ✓' : 'Share via Email')}
                        </Button>
                    )}
                </Box>
            </Box>
        );
    };

    // =============== MAIN RENDER ===============

    const renderStepContent = () => {
        if (showSuccess) return renderSuccessScreen();
        if (currentStep === -1) return renderWelcomeScreen();
        if (currentStep === 0) return renderAdminDetailsStep();
        if (currentStep === 1) return renderStaffDetailsStep();
        if (currentStep === 2) return renderDriveLinkStep();
        return null;
    };

    const renderFooterButtons = () => {
        if (showSuccess || currentStep === -1 || currentStep === 2) return null;

        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: isMobile ? 2 : 3,
                mt: 3,
                pt: 2,
                pb: 0,
                borderTop: '1px solid #E0E0E0',
            }}>
                {currentStep > 0 && (
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        sx={{
                            width: isMobile ? '100px' : '120px',
                            height: isMobile ? '44px' : '48px',
                            borderRadius: '8px',
                            fontSize: isMobile ? '14px' : '16px',
                            borderWidth: '1px',
                            borderColor: '#B00020',
                            color: '#B00020',
                            fontFamily: 'Roboto',
                            lineHeight: '24px',
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'inherit',
                                color: '#B00020',
                                boxShadow: 10,
                            },
                        }}
                    >
                        Back
                    </Button>
                )}

                {currentStep === 0 && (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!isAdminDetailsValid()}
                        sx={{
                            width: isMobile ? '100px' : '120px',
                            height: isMobile ? '44px' : '48px',
                            borderRadius: '8px',
                            fontSize: isMobile ? '14px' : '16px',
                            backgroundColor: '#0063F7 !important',
                            color: '#FFFFFF',
                            fontFamily: 'Roboto',
                            lineHeight: '24px',
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#0051d4 !important' },
                            '&.Mui-disabled': { backgroundColor: '#E0E0E0 !important', color: '#9E9E9E' },
                            px: isMobile ? 3 : 4,
                        }}
                    >
                        Next
                    </Button>
                )}

                {currentStep === 1 && (
                    <Button
                        variant="contained"
                        onClick={handleSubmitStaff}
                        disabled={submitting || onboardingLoading || (hasAnyStaffData() && !areAllStaffValid())}
                        startIcon={(submitting || onboardingLoading) ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            width: isMobile ? '140px' : '160px',
                            height: isMobile ? '44px' : '48px',
                            borderRadius: '8px',
                            fontSize: isMobile ? '14px' : '16px',
                            backgroundColor: '#0063F7 !important',
                            color: '#FFFFFF',
                            fontFamily: 'Roboto',
                            lineHeight: '24px',
                            letterSpacing: '0.01em',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#0051d4 !important' },
                            '&.Mui-disabled': { backgroundColor: '#E0E0E0 !important', color: '#9E9E9E' },
                            px: isMobile ? 3 : 4,
                        }}
                    >
                        {(submitting || onboardingLoading) ? 'Creating...' : 'Continue'}
                    </Button>
                )}
            </Box>
        );
    };

    return (
        <>
            {/* Main Onboarding Dialog */}
            <Dialog
                open={open && !showSuccess}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        padding: isMobile ? '16px' : '32px',
                        margin: isMobile ? '16px' : '32px',
                        width: isMobile ? 'calc(100% - 32px)' : '520px',
                    },
                }}
            >
                {/* Header with stepper — shown only when past welcome */}
                {currentStep >= 0 && (
                    <DialogTitle sx={{
                        pb: 2,
                        pt: 0,
                        px: 0,
                        fontFamily: 'Roboto',
                        fontWeight: 600,
                        fontSize: isMobile ? '20px' : '24px',
                    }}>
                        <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 0 }}>
                            {STEPS.map((label) => (
                                <Step key={label}>
                                    <StepLabel
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Roboto',
                                            },
                                            '& .MuiStepIcon-root.Mui-active': {
                                                color: '#0063F7',
                                            },
                                            '& .MuiStepIcon-root.Mui-completed': {
                                                color: '#4CAF50',
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </DialogTitle>
                )}

                <DialogContent sx={{
                    pt: currentStep >= 0 ? 0 : 0,
                    px: 0,
                    pb: 0,
                    overflowY: 'auto',
                    ...(currentStep === -1 && {
                        display: 'flex',
                        flexDirection: 'column',
                    }),
                }}>
                    {renderStepContent()}
                    {renderFooterButtons()}
                </DialogContent>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={showSuccess}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: isMobile ? '16px' : '32px',
                        margin: isMobile ? '16px' : '32px',
                        width: isMobile ? 'calc(100% - 32px)' : '520px',
                    },
                }}
            >
                <DialogContent sx={{
                    px: 0,
                }}>
                    {renderSuccessScreen()}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default OnboardingModal;
